// The `{"data": …}` envelope the API answers records and lists with
// (`API.ResponseList` / `API.ResponseSingle`), and what a reader does when a
// 2xx does not carry it.
//
// #415 — a 2xx whose body is not that envelope leaves `res.data.data`
// undefined. It is not a hypothetical body: axios hands `''` over for an empty
// response and the raw string for the HTML a proxy serves, because its default
// transform keeps the text when `JSON.parse` throws. Committing that takes a
// store's state out of the type it was born with, and the failure surfaces far
// from here — in a render (#415), or inside the `commit` itself (#419).
//
// This is a check each reader makes with the shape it needs, not something a
// response interceptor could do once for everyone: the expected shape is the
// caller's knowledge, and not every endpoint even uses the envelope (`/auth`
// answers with its tokens at the top level, and `refreshSession` in
// `request.js` guards that one the same way, at the read).

export const ARRAY_SHAPE = {
  matches: (data) => Array.isArray(data),
  empty: () => [],
}

export const OBJECT_SHAPE = {
  matches: (data) => data !== null && typeof data === 'object' && !Array.isArray(data),
  empty: () => ({}),
}

/**
 * Read `res.data.data`, checking it is the shape the caller needs.
 *
 * Returns `{ok, data}`. `data` is the payload when `ok`, and `shape.empty()`
 * when not — never `undefined`, so a caller that commits it cannot change the
 * type of what it commits into.
 *
 * `where` names the reader for the log, e.g. `members.read GET members/<id>`.
 */
export function readEnvelope(res, shape, where) {
  const data = res?.data?.data
  if (shape.matches(data)) {
    return { ok: true, data }
  }
  // The request succeeded, so there is no rejection to report. Without this the
  // fault leaves no trace at all and its only symptom is the damage it does
  // downstream, which is how #415 reached production as a render TypeError.
  console.error(`${where} answered ${res?.status} without a usable envelope`, res?.data)
  return { ok: false, data: shape.empty() }
}
